import moment from 'moment-timezone';
import * as userAgentHelper from 'userAgentHelper';
import angularModule from './authentication_module';

angularModule.factory('AuthFormHelperMixin', [
    '$injector',

    function factory($injector) {
        const $q = $injector.get('$q');
        const $rootScope = $injector.get('$rootScope');
        const safeApply = $injector.get('safeApply');
        const AuthClient = $injector.get('AuthClient');
        const $window = $injector.get('$window');
        const omniauth = $injector.get('omniauth');
        const ConfigFactory = $injector.get('ConfigFactory');
        const blockAuthenticatedAccess = $injector.get('blockAuthenticatedAccess');
        const reactRouterBridge = $injector.get('reactRouterBridge');
        const TranslationHelper = $injector.get('TranslationHelper');
        const ErrorLogService = $injector.get('ErrorLogService');
        const ClientStorage = $injector.get('ClientStorage');
        const EventLogger = $injector.get('EventLogger');
        const HttpQueue = $injector.get('HttpQueue');

        function safeStringify(value) {
            try {
                return JSON.stringify(value);
            } catch {
                return `Unserializable value: ${typeof value}`;
            }
        }

        // Native OAuth plugin user-cancel signals — these vary by provider/platform.
        const PLUGIN_CANCEL_CODES = [
            'cancel',
            '1001', // iOS SIWA cancel code
        ];

        // 403 = server-side account-state rejections (deactivated, in deletion queue).
        const isIgnorableError = error => error?.status === 403 || PLUGIN_CANCEL_CODES.includes(error);

        const translationHelper = new TranslationHelper('authentication.auth_form_helper_mixin');

        return {
            onLink(scope, onOmniauthSuccess) {
                scope.preventSubmit = false;

                scope.form_errors = {};

                if (blockAuthenticatedAccess.block) {
                    scope.form_errors.general = blockAuthenticatedAccess.errorMessage;
                    scope.preventSubmit = true;
                }

                // Auth::OmniauthCallbacksController#redirect_failure passes back an already-localized error_description.
                if (reactRouterBridge.search().error_description) {
                    scope.form_errors.general = reactRouterBridge.search().error_description;
                }

                // scoped for testing
                scope.getNonceValue = () => Math.floor(Math.random() * (Number.MAX_SAFE_INTEGER - 1)) + 1;

                // needed on scope for tests
                scope.getNativeOauthPluginPromise = (provider, signUpCode) => {
                    const failedToRetrieveAuthTokenErrorMessage = 'Failed to retrieve auth token from (native)';
                    const failedToValidateClientNonce = 'Failed to validate client nonce for FB Limited Login (native)';
                    const loginFields = ['public_profile', 'email'];
                    const baseParams = {
                        sign_up_code: signUpCode,
                        timezone: moment.tz.guess(),
                    };

                    // Facebook Native
                    if (provider === 'facebook' && window.facebookConnectPlugin) {
                        return $q((resolve, reject) => {
                            // validate login and kick off access token callbacks
                            if (userAgentHelper.isiOSoriPadOSDevice()) {
                                const nonceVal = `${scope.getNonceValue()}`;

                                window.facebookConnectPlugin.loginWithLimitedTracking(
                                    loginFields,
                                    nonceVal,
                                    loginResponse => {
                                        if (nonceVal !== loginResponse?.authResponse?.nonce) {
                                            ErrorLogService.notify(failedToValidateClientNonce, undefined, {
                                                provider,
                                                expectedNonce: nonceVal,
                                                providedNonce: loginResponse?.authResponse?.nonce,
                                            });
                                            reject('Validation failed');
                                        }

                                        resolve({
                                            ...baseParams,
                                            authentication_token: loginResponse.authResponse?.authenticationToken,
                                            nonce: nonceVal,
                                        });
                                    },
                                    reject,
                                );
                            } else {
                                window.facebookConnectPlugin.login(
                                    loginFields,
                                    () => {
                                        // now get the token
                                        window.facebookConnectPlugin.getAccessToken(
                                            accessToken => {
                                                resolve({ ...baseParams, access_token: accessToken });
                                            },
                                            tokenError => {
                                                ErrorLogService.notify(
                                                    failedToRetrieveAuthTokenErrorMessage,
                                                    undefined,
                                                    {
                                                        tokenError: safeStringify(tokenError),
                                                        provider,
                                                    },
                                                );
                                                reject(tokenError);
                                            },
                                        );
                                    },
                                    reject,
                                );
                            }
                        });
                    }

                    // Google Native
                    if (provider === 'google_oauth2' && window.plugins && window.plugins.googleplus) {
                        return $q((resolve, reject) => {
                            // get the clientId required to get idToken values in the response
                            const webClientId = ConfigFactory.getSync().google_oauth_id;

                            // Google is a little annoying in that the webClient is needed, as well as the plugin configuration pointing to a
                            // SHA-1 mapped token generated from production certificates. ie - the webClientId needs to match the same "production"
                            // environment. Be sure you account for this in application.env if testing native Google Sign-In
                            window.plugins.googleplus.login(
                                {
                                    // NOTE: this is only used by Android and will be reflected as the aud in identity token
                                    webClientId,
                                },
                                providerResponse => {
                                    resolve({ ...baseParams, provider_data: providerResponse });
                                },
                                reject,
                            );
                        });
                    }

                    // Apple Native
                    // Note that Android does not have native support for Sign In With Apple, so we do not expose Apple as an OAuth provider
                    // on Native Android.
                    if (provider === 'apple_quantic' && window.cordova?.plugins?.SignInWithApple) {
                        return $q((resolve, reject) => {
                            window.cordova.plugins.SignInWithApple.signin(
                                { requestedScopes: [0, 1] },
                                providerResponse => {
                                    resolve({ ...baseParams, provider_data: providerResponse });
                                },
                                reject,
                            );
                        });
                    }

                    throw new Error('Unsupported native provider');
                };

                scope.loginWithProvider = (provider, signUpCode, programType) => {
                    // We know that it is possible for the signUpCode to not be set yet in
                    // CORDOVA, but that shouldn't be a problem as we default the signUpCode on
                    // the server-side if it doesn't exist. See - https://trello.com/c/JDHMbJQr
                    if (!signUpCode && !$window.CORDOVA) {
                        ErrorLogService.notify('loginWithProvider called with no signUpCode');
                    }

                    scope.preventSubmit = true;

                    // Since logging in with oauth will send us away from the page,
                    // try to make extra sure these events get logged.
                    EventLogger.log('auth:login_with_provider', { provider });

                    // Note that we need to wait for the request to finish before starting the OAuth
                    // flow because of concurrency issues with the session state (which is actually
                    // only needed for OAuth). Theoretically it's not possible right now to completely
                    // eliminate concurrency issues here because auth requests are exempt from the HttpQueue,
                    // and the status ping could theoretically happen at just the right time, and tinyEventLogger
                    // requests are outside Angular and thus not using the HttpQueue. But practically this
                    // particular events request is the main offender since it's happening at the same time we
                    // initiate OAuth.
                    // See https://trello.com/c/905quJK9
                    // BUT, if the queue is blocked by a failed request (and so we're showing the login modal), then
                    // we can't flush the events (and we may want to, since we may have failed to save something else)
                    // see https://trello.com/c/6SKC69NI
                    const promise = HttpQueue.blockedByFailedRequest ? $q.resolve() : EventLogger.tryToSaveBuffer();

                    // Initiate Omniauth on web
                    if (!window.CORDOVA) {
                        return promise.then(() => omniauth.loginWithProvider(provider, signUpCode, programType));
                    }

                    // Initiate native OAuth plugin on mobile
                    return promise
                        .then(() => scope.getNativeOauthPluginPromise(provider, signUpCode))
                        .then(params => AuthClient.exchangeNativeOauth(provider, params))
                        .then(user => {
                            $rootScope.$broadcast('auth:login-success', user);
                            safeApply($rootScope);

                            scope.preventSubmit = false;
                            scope.form_errors = {};

                            // Wait for ValidationResponder to tell us we've authenticated, to ensure
                            // we have a currentUser before running the success callback
                            const stopListeningForLoginSuccess = $rootScope.$on(
                                'validation-responder:login-success',
                                () => {
                                    if (onOmniauthSuccess) {
                                        onOmniauthSuccess.call(this);
                                    }
                                    stopListeningForLoginSuccess();
                                },
                            );
                            scope.$on('$destroy', () => {
                                stopListeningForLoginSuccess();
                            });
                        })
                        .catch(error => {
                            scope.preventSubmit = false;

                            // clear any temporary redirect targets
                            ClientStorage.removeItem('oauth_redirect_target');

                            // Auth::NativeOauthExchangesController#exchange renders an already-localized error_description on failure.
                            // Fall back to a generic message on transport errors that arrive without a response body,
                            // but skip the fallback for user-cancellations (they shouldn't surface as form errors).
                            if (error.data?.error_description) {
                                scope.form_errors.general = error.data.error_description;
                            } else if (!isIgnorableError(error)) {
                                scope.form_errors.general = translationHelper.get('server_error');
                            }

                            safeApply($rootScope);

                            if (!isIgnorableError(error)) {
                                ErrorLogService.notify('Failed to login (native)', undefined, {
                                    parsedError: safeStringify(error),
                                    provider,
                                });
                            }
                        });
                };

                scope.formErrorClasses = fieldName => {
                    if (scope.form_errors[fieldName]) {
                        return ['form-error', 'active'];
                    }
                    return ['form-error'];
                };

                scope.formValidationClasses = fieldName => {
                    if (scope.form_errors[fieldName]) {
                        return ['form-control', 'ng-invalid'];
                    }
                    return ['form-control'];
                };

                scope.uniqueErrors = errors => {
                    if (Array.isArray(errors)) {
                        errors = [...new Set(errors)].join('\n');
                    }
                    return errors;
                };

                scope.submitLogin = signInInfo => {
                    scope.preventSubmit = true;
                    scope.form_errors = {};

                    if (!signInInfo.provider) {
                        signInInfo.provider = scope.urlConfig.provider;
                    }

                    return (
                        AuthClient.signIn(signInInfo)
                            // using then/catch here rather than .finally to prevent
                            // rejected promises from bubbling up to the caller
                            .then(() => {
                                scope.preventSubmit = false;
                                safeApply($rootScope);
                            })
                            .catch(() => {
                                scope.preventSubmit = false;
                                safeApply($rootScope);
                            })
                    );
                };

                // FrontRoyalApiErrorHandler ignores /auth/* URLs, so we surface the sign-in error here.
                // Auth::SessionsController#create renders an already-localized `error_description` for every failure mode;
                // we fall back to a generic message on transport errors that arrive without a response body.
                const errorListener = $rootScope.$on('auth:login-error', (_event, response) => {
                    scope.form_errors.general =
                        response?.data?.error_description || translationHelper.get('server_error');
                });

                scope.$on('$destroy', () => {
                    if (errorListener) {
                        errorListener();
                    }
                });
            },
        };
    },
]);
